/**
 * API Endpoint Contracts
 * ============================================================================
 *
 * Purpose:
 * --------
 * Defines all client-side API endpoint paths used to communicate with the
 * backend. These endpoints act as a stable contract between frontend features
 * and backend services.
 *
 * Design principles:
 * ------------------
 * - Endpoints are centralized to prevent string duplication.
 * - UI code must not hardcode endpoint paths.
 * - Dynamic segments are expressed as typed functions.
 *
 * Non-goals:
 * ----------
 * - Making network requests
 * - Handling request logic or side effects
 *
 * Any changes in this file may impact multiple features and should be
 * coordinated carefully.
 * ============================================================================
 */

export const API_ENDPOINTS = {
  /**
   * Authentication & session management endpoints.
   * Used by login, registration, and session recovery flows.
   */
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    REGISTER: '/auth/register',
    VERIFY_EMAIL: '/auth/verify-email',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    ME: '/auth/me',
  },

  /**
   * Policy management endpoints.
   * Covers policy lifecycle actions and related resources.
   */
  POLICIES: {
    BASE: '/policies',
    BY_ID: (id: string) => `/policies/${id}`,
    CALCULATE_PREMIUM: '/policies/calculate-premium',
    RENEW: (id: string) => `/policies/${id}/renew`,
    CANCEL: (id: string) => `/policies/${id}/cancel`,
    SUSPEND: (id: string) => `/policies/${id}/suspend`,
    ACTIVATE: (id: string) => `/policies/${id}/activate`,
    DOCUMENTS: (id: string) => `/policies/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/policies/${id}/documents/upload`,
    HISTORY: (id: string) => `/policies/${id}/history`,
    ENDORSEMENTS: (id: string) => `/policies/${id}/endorsements`,
  },

  /**
   * Claims handling endpoints.
   * Used for claim submission, assessment, and lifecycle updates.
   */
  CLAIMS: {
    BASE: '/claims',
    BY_ID: (id: string) => `/claims/${id}`,
    SUBMIT: '/claims/submit',
    APPROVE: (id: string) => `/claims/${id}/approve`,
    REJECT: (id: string) => `/claims/${id}/reject`,
    UPDATE_STATUS: (id: string) => `/claims/${id}/status`,
    DOCUMENTS: (id: string) => `/claims/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/claims/${id}/documents/upload`,
    HISTORY: (id: string) => `/claims/${id}/history`,
    ASSESSMENT: (id: string) => `/claims/${id}/assessment`,
  },

  /**
   * Customer-facing endpoints.
   * Used for managing customer profiles, documents, and related activity.
   */
  CUSTOMERS: {
    BASE: '/customers',
    BY_ID: (id: string) => `/customers/${id}`,
    POLICIES: (id: string) => `/customers/${id}/policies`,
    CLAIMS: (id: string) => `/customers/${id}/claims`,
    VERIFY: '/customers/verify',
    VERIFY_ID: (id: string) => `/customers/${id}/verify`,
    DOCUMENTS: (id: string) => `/customers/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/customers/${id}/documents/upload`,
    ACTIVITY: (id: string) => `/customers/${id}/activity`,
  },

  /**
   * Product catalog endpoints.
   * Read-only endpoints used for quoting and policy creation flows.
   */
  PRODUCTS: {
    BASE: '/products',
    BY_ID: (id: string) => `/products/${id}`,
    ACTIVE: '/products/active',
    CATEGORIES: '/products/categories',
    COVERAGE_OPTIONS: (id: string) => `/products/${id}/coverage-options`,
  },

  /**
   * Internal user and agent management endpoints.
   * Not exposed to customer-facing features.
   */
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    CHANGE_PASSWORD: '/users/change-password',
    PERMISSIONS: '/users/permissions',
    ROLES: '/users/roles',
  },

  /**
   * File upload support endpoints.
   * Used for presigned uploads and post-upload confirmation.
   */
  UPLOAD: {
    PRESIGNED_URL: '/upload/presigned-url',
    CONFIRM: '/upload/confirm',
    DIRECT: '/upload/direct',
  },

  /**
   * Notification endpoints.
   * Used for in-app notifications and read-state management.
   */
  NOTIFICATIONS: {
    BASE: '/notifications',
    BY_ID: (id: string) => `/notifications/${id}`,
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    UNREAD_COUNT: '/notifications/unread-count',
  },

  /**
   * Dashboard and analytics endpoints.
   * Aggregated, read-only data for reporting and visualization.
   */
  DASHBOARD: {
    STATS: '/dashboard/stats',
    RECENT_ACTIVITIES: '/dashboard/recent-activities',
    CHARTS: '/dashboard/charts',
  },

  /**
   * User-configurable application settings.
   */
  SETTINGS: {
    BASE: '/settings',
    PREFERENCES: '/settings/preferences',
    NOTIFICATIONS: '/settings/notifications',
    SECURITY: '/settings/security',
  },

  /**
   * Business onboarding endpoints.
   * Used for business registration and email verification flows.
   */
  ONBOARDING: {
    REGISTER: '/onboarding/register',
    VERIFY_EMAIL: '/onboarding/verify-email',
    RESEND_VERIFICATION: '/onboarding/resend-verification',
    STATUS: '/onboarding/status',
    SUBMIT: '/onboarding/submit',
    TEAM_ACCEPT: '/onboarding/team/accept',
  },
} as const;

/**
 * Builds a query string from optional parameters.
 *
 * Undefined, null, and empty values are intentionally omitted to
 * avoid leaking meaningless query parameters to the backend.
 */
export const buildQueryString = (params: Record<string, unknown>): string => {
  const filtered = Object.entries(params).filter(
    ([_, value]) => value !== undefined && value !== null && value !== ''
  );

  if (filtered.length === 0) {
    return '';
  }

  const queryString = filtered
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  return `?${queryString}`;
};

/**
 * Type contract for endpoint builders with dynamic path segments.
 *
 * Ensures consistency for functions that generate resource-specific URLs.
 */
export type EndpointFunction = (id: string) => string;
